import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Lock, Menu, X } from 'lucide-react'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { ReservedAreaLink } from '@/components/ReservedAreaLink'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

// Voci del menu: ogni chiave esiste in src/i18n/routes.ts e ha una traduzione `nav.*`.
const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'stoccoPong', labelKey: 'nav.stoccoPong' },
  { key: 'mimiWheel', labelKey: 'nav.mimiWheel' },
]

export function CompassMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 4l2 8-2 8-2-8z" fill="currentColor" />
    </svg>
  )
}

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const currentSlug = location.pathname.split('/').filter(Boolean)[1] ?? ''
  const menuToggleRef = useRef<HTMLButtonElement>(null)
  const wasMobileOpenRef = useRef(false)

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => setIsMobileOpen(false), [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  // Esc chiude il menu; mentre è aperto il resto della pagina è inert (Tab non può raggiungerlo);
  // alla chiusura il focus torna sul pulsante che l'ha aperto.
  useEffect(() => {
    const pageElements = document.querySelectorAll('main, footer')
    pageElements.forEach((el) => el.toggleAttribute('inert', isMobileOpen))
    if (!isMobileOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      pageElements.forEach((el) => el.removeAttribute('inert'))
    }
  }, [isMobileOpen])

  useEffect(() => {
    if (wasMobileOpenRef.current && !isMobileOpen) {
      menuToggleRef.current?.focus()
    }
    wasMobileOpenRef.current = isMobileOpen
  }, [isMobileOpen])

  const isActive = (key: RouteKey) => {
    const route = localizedRoutes[key]
    return route.it === currentSlug || route.en === currentSlug
  }

  // Con il menu aperto niente backdrop-filter: renderebbe l'header il riferimento del pannello `fixed`
  // (#site-menu), che collasserebbe sui 64px dell'header invece di coprire lo schermo.
  return (
    <header className={cn('fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300', isScrolled ? 'bg-paper/90 shadow-[0_1px_0_var(--color-line)] backdrop-blur-md' : 'bg-transparent', isMobileOpen && 'max-lg:bg-ink max-lg:shadow-none max-lg:backdrop-blur-none')}>
      <nav aria-label={t('aria.mainNavigation')} className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20 lg:px-8">
        <div className="lg:flex-1">
          <LocalizedLink to="home" className={cn('inline-flex items-center gap-2 font-display text-xl font-semibold tracking-tight text-ink transition-colors md:text-2xl', isMobileOpen && 'max-lg:text-paper')}>
            <CompassMark />
            <span>S &amp; M</span>
          </LocalizedLink>
        </div>

        <div className="hidden items-center gap-1 lg:flex">
          {navItems.map(({ key, labelKey }) => (
            <LocalizedLink
              key={key}
              to={key}
              aria-current={isActive(key) ? 'page' : undefined}
              className={cn('group relative rounded-md px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-ink/5', isActive(key) && 'font-semibold')}
            >
              {t(labelKey)}
              <span className={cn('absolute inset-x-3 bottom-1 h-0.5 rounded-full bg-sea transition-transform', isActive(key) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100')} />
            </LocalizedLink>
          ))}
        </div>

        <div className="hidden items-center justify-end gap-2 lg:flex lg:flex-1">
          <LanguageSelector variant="desktop" />
          <ReservedAreaLink className="inline-flex items-center gap-1.5 rounded-md border border-ink px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-paper">
            <Lock className="size-3.5" aria-hidden="true" />
            {t('nav.reservedArea')}
          </ReservedAreaLink>
        </div>

        <button
          ref={menuToggleRef}
          type="button"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className={cn('rounded-md p-2 text-ink transition-colors hover:bg-ink/5 lg:hidden', isMobileOpen && 'text-paper hover:bg-paper/10')}
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
          aria-controls="site-menu"
        >
          {isMobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </nav>

      {/* reducedMotion "user": con la preferenza attiva restano solo le dissolvenze, niente spostamenti. */}
      <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            id="site-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 top-16 z-40 flex flex-col overflow-y-auto bg-ink px-4 pt-4 pb-6 text-paper sm:px-6 md:top-20 lg:hidden"
          >
            <p aria-hidden="true" className="flex justify-between border-b border-paper/15 pb-2 font-body text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-paper/60">
              <span>{t('nav.departures')}</span>
              <span className="text-sun">SM 0310</span>
            </p>
            <motion.ol
              aria-label={t('aria.mobileNavigation')}
              initial="hidden"
              animate="shown"
              variants={{ shown: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } }}
              className="flex-1"
            >
              {navItems.map(({ key, labelKey }, i) => (
                <motion.li
                  key={key}
                  variants={{ hidden: { opacity: 0, y: 12 }, shown: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } } }}
                  className="border-b border-paper/15"
                >
                  <LocalizedLink to={key} aria-current={isActive(key) ? 'page' : undefined} className="group flex items-center gap-4 py-4">
                    <span aria-hidden="true" className="flex gap-0.5 font-body text-sm font-semibold leading-none">
                      {String(i + 1).padStart(2, '0').split('').map((char, j) => (
                        <span key={j} className="grid h-7 w-5 place-items-center rounded-[3px] bg-ink-soft/35">{char}</span>
                      ))}
                    </span>
                    <span className={cn('flex-1 font-display text-[clamp(2rem,9vw,3rem)] leading-none tracking-tight transition-transform duration-300 ease-out-expo group-hover:translate-x-1', isActive(key) && 'italic text-sun')}>
                      {t(labelKey)}
                    </span>
                    {isActive(key) ? (
                      <span aria-hidden="true" className="flex items-center gap-1.5 font-body text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-sun">
                        <span className="size-1.5 rounded-full bg-sun motion-safe:animate-pulse" />
                        {t('nav.youAreHere')}
                      </span>
                    ) : (
                      <ArrowRight aria-hidden="true" className="size-5 text-paper/60 transition-transform duration-300 ease-out-expo group-hover:translate-x-1" />
                    )}
                  </LocalizedLink>
                </motion.li>
              ))}
            </motion.ol>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 overflow-hidden rounded-xl border-2 border-paper bg-paper text-ink [--notch-bg:var(--color-ink)]"
            >
              <p className="bg-sea/15 px-4 pt-3 pb-2.5 font-body text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-sea-ink">{t('language.label')}</p>
              <span aria-hidden="true" className="ticket-perforation block" />
              <div className="px-4 pb-4">
                <LanguageSelector variant="mobile" />
                <ReservedAreaLink className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ink px-4 py-3 text-base font-medium text-paper transition-colors hover:bg-ink/90">
                  <Lock className="size-4" aria-hidden="true" />
                  {t('nav.reservedArea')}
                </ReservedAreaLink>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </MotionConfig>
    </header>
  )
}
